import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { comics } from '@/mocks/comics';

export default function ChapterReaderPage() {
  const { t } = useTranslation();
  const { slug, chapterId } = useParams<{ slug: string; chapterId: string }>();

  const comic = comics.find((c) => c.slug === slug);
  const chapterNum = parseInt(chapterId || '1', 10);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <p className="text-lg text-foreground-600 mb-4">找不到该漫画</p>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const chapter = comic.chapters.find((c) => c.id === chapterNum);
  const totalChapters = comic.chapters.length;
  const hasPrev = chapterNum > 1;
  const hasNext = chapterNum < totalChapters;
  const prevChapter = comic.chapters.find((c) => c.id === chapterNum - 1);
  const nextChapter = comic.chapters.find((c) => c.id === chapterNum + 1);

  return (
    <>
      <SchemaOrg
        type="Article"
        data={{
          headline: `${comic.title} ${chapter?.title || `第${chapterNum}话`} - 八仔漫画网`,
          description: `在线阅读${comic.title}${chapter?.title || `第${chapterNum}话`}，八仔漫画网提供高清漫画免费在线浏览。`,
          image: comic.cover,
          author: { '@type': 'Person', name: comic.author },
          datePublished: chapter?.updatedAt || '2026-06-28',
          dateModified: chapter?.updatedAt || '2026-06-28',
          inLanguage: 'zh-CN',
          publisher: {
            '@type': 'Organization',
            name: '八仔漫画网',
            logo: {
              '@type': 'ImageObject',
              url: 'http://jisutxt.com/logo.png',
            },
          },
          isPartOf: {
            '@type': 'ComicSeries',
            name: comic.title,
            url: `http://jisutxt.com/comic/${comic.slug}`,
          },
          genre: comic.category,
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `http://jisutxt.com/comic/${comic.slug}/chapter/${chapterNum}`,
          },
        }}
      />

      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: t('breadcrumb.home'),
              item: 'http://jisutxt.com/',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: comic.category,
              item: `http://jisutxt.com/category/${comic.categorySlug}`,
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: comic.title,
              item: `http://jisutxt.com/comic/${comic.slug}`,
            },
            {
              '@type': 'ListItem',
              position: 4,
              name: chapter?.title || `第${chapterNum}话`,
              item: `http://jisutxt.com/comic/${comic.slug}/chapter/${chapterNum}`,
            },
          ],
        }}
      />

      <div className="min-h-screen bg-background-900">
        <Navbar />

        <main className="pt-16">
          <Breadcrumb
            items={[
              { label: t('breadcrumb.home'), href: '/' },
              { label: comic.category, href: `/category/${comic.categorySlug}` },
              { label: comic.title, href: `/comic/${comic.slug}` },
              { label: chapter?.title || `第${chapterNum}话` },
            ]}
          />

          <div className="max-w-4xl mx-auto px-4 py-6">
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-lg md:text-xl font-heading font-bold text-background-50">
                {comic.title} - {chapter?.title || `第${chapterNum}话`}
              </h1>
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 flex items-center justify-center ${hasPrev ? 'cursor-pointer' : 'opacity-30 cursor-not-allowed'}`}>
                  {hasPrev ? (
                    <Link to={`/comic/${comic.slug}/chapter/${prevChapter?.id}`} className="w-full h-full flex items-center justify-center">
                      <i className="ri-arrow-left-s-line text-background-50 text-lg"></i>
                    </Link>
                  ) : (
                    <i className="ri-arrow-left-s-line text-background-50 text-lg"></i>
                  )}
                </div>
                <span className="text-sm text-background-400">
                  {chapterNum} / {totalChapters}
                </span>
                <div className={`w-8 h-8 flex items-center justify-center ${hasNext ? 'cursor-pointer' : 'opacity-30 cursor-not-allowed'}`}>
                  {hasNext ? (
                    <Link to={`/comic/${comic.slug}/chapter/${nextChapter?.id}`} className="w-full h-full flex items-center justify-center">
                      <i className="ri-arrow-right-s-line text-background-50 text-lg"></i>
                    </Link>
                  ) : (
                    <i className="ri-arrow-right-s-line text-background-50 text-lg"></i>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-background-800 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <Link
                  to={`/comic/${comic.slug}`}
                  className="inline-flex items-center gap-1 text-sm text-background-300 hover:text-background-50 transition-colors whitespace-nowrap"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-left-line text-xs"></i>
                  </div>
                  {t('comic.backToList')}
                </Link>

                <div className="flex items-center gap-2">
                  <Link
                    to={`/comic/${comic.slug}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium bg-background-700 text-background-200 hover:bg-background-600 transition-colors whitespace-nowrap"
                  >
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-list-check text-xs"></i>
                    </div>
                    {t('comic.chapterList')}
                  </Link>
                </div>
              </div>
            </div>

            <div className="space-y-1">
              <div className="bg-background-800 rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Chinese%20manhua%20comic%20page%20featuring%20dynamic%20action%20scene%20with%20magical%20energy%20effects%2C%20black%20and%20white%20manga%20style%20illustration%20with%20screen%20tones%2C%20dramatic%20composition%20with%20speed%20lines%2C%20clean%20line%20art%2C%20speech%20bubbles%20with%20Chinese%20text&width=1200&height=900&seq=manga-chapter-page-01&orientation=landscape"
                  alt="漫画页面 1"
                  title="全职法师漫画 - 八仔漫画网"
                  className="w-full h-auto"
                  loading="lazy"
                />
              </div>
              <div className="bg-background-800 rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Chinese%20manhua%20comic%20page%20showing%20character%20close-up%20with%20intense%20expression%2C%20black%20and%20white%20manga%20style%2C%20detailed%20facial%20features%2C%20screen%20tone%20shading%2C%20motion%20lines%2C%20speech%20bubbles&width=1200&height=900&seq=manga-chapter-page-02&orientation=landscape"
                  alt="漫画页面 2"
                  title="全职法师漫画 - 八仔漫画网"
                  className="w-full h-auto"
                  loading="lazy"
                />
              </div>
              <div className="bg-background-800 rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Chinese%20manhua%20comic%20page%20with%20battle%20scene%20between%20two%20mages%2C%20fire%20and%20ice%20magic%20effects%2C%20black%20and%20white%20manga%20style%2C%20dynamic%20camera%20angle%2C%20sound%20effects%20text%2C%20screen%20tone%20background&width=1200&height=900&seq=manga-chapter-page-03&orientation=landscape"
                  alt="漫画页面 3"
                  title="全职法师漫画 - 八仔漫画网"
                  className="w-full h-auto"
                  loading="lazy"
                />
              </div>
              <div className="bg-background-800 rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Chinese%20manhua%20comic%20page%20depicting%20magic%20academy%20classroom%20scene%2C%20students%20practicing%20spells%2C%20black%20and%20white%20manga%20style%2C%20detailed%20background%2C%20screen%20tones%2C%20multiple%20panel%20layout&width=1200&height=900&seq=manga-chapter-page-04&orientation=landscape"
                  alt="漫画页面 4"
                  title="全职法师漫画 - 八仔漫画网"
                  className="w-full h-auto"
                  loading="lazy"
                />
              </div>
              <div className="bg-background-800 rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Chinese%20manhua%20comic%20page%20with%20character%20transformation%20sequence%2C%20energy%20burst%20effect%2C%20black%20and%20white%20manga%20style%2C%20dramatic%20lighting%2C%20speed%20lines%2C%20action%20pose&width=1200&height=900&seq=manga-chapter-page-05&orientation=landscape"
                  alt="漫画页面 5"
                  title="全职法师漫画 - 八仔漫画网"
                  className="w-full h-auto"
                  loading="lazy"
                />
              </div>
              <div className="bg-background-800 rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Chinese%20manhua%20comic%20page%20showing%20fantasy%20landscape%20with%20ancient%20ruins%2C%20characters%20exploring%2C%20black%20and%20white%20manga%20style%2C%20detailed%20architecture%2C%20atmospheric%20shading%2C%20wide%20panel&width=1200&height=900&seq=manga-chapter-page-06&orientation=landscape"
                  alt="漫画页面 6"
                  title="全职法师漫画 - 八仔漫画网"
                  className="w-full h-auto"
                  loading="lazy"
                />
              </div>
              <div className="bg-background-800 rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Chinese%20manhua%20comic%20page%20with%20dramatic%20climax%20moment%2C%20character%20casting%20ultimate%20spell%2C%20black%20and%20white%20manga%20style%2C%20double%20page%20spread%20effect%2C%20impact%20lines%2C%20explosive%20energy&width=1200&height=900&seq=manga-chapter-page-07&orientation=landscape"
                  alt="漫画页面 7"
                  title="全职法师漫画 - 八仔漫画网"
                  className="w-full h-auto"
                  loading="lazy"
                />
              </div>
              <div className="bg-background-800 rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Chinese%20manhua%20comic%20page%20with%20cliffhanger%20ending%2C%20mysterious%20silhouette%20appearing%2C%20black%20and%20white%20manga%20style%2C%20dark%20atmosphere%2C%20dramatic%20reveal%2C%20chapter%20end%20marker&width=1200&height=900&seq=manga-chapter-page-08&orientation=landscape"
                  alt="漫画页面 8"
                  title="全职法师漫画 - 八仔漫画网"
                  className="w-full h-auto"
                  loading="lazy"
                />
              </div>
            </div>

            <div className="flex items-center justify-between mt-8 pt-6 border-t border-background-700">
              {hasPrev ? (
                <Link
                  to={`/comic/${comic.slug}/chapter/${prevChapter?.id}`}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium bg-background-800 text-background-200 hover:bg-background-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-left-line text-xs"></i>
                  </div>
                  {t('comic.prevChapter')}
                </Link>
              ) : (
                <div></div>
              )}

              {hasNext ? (
                <Link
                  to={`/comic/${comic.slug}/chapter/${nextChapter?.id}`}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium bg-primary-500 text-white hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                >
                  {t('comic.nextChapter')}
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-xs"></i>
                  </div>
                </Link>
              ) : (
                <div></div>
              )}
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}